import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { Heading } from '@mysten/ui';

import type { ClobTradeRow } from '@cpc/tx';
import { useT } from '~/i18n/LocaleContext';
import { Tabs, TabsContent } from '~/ui/Tabs';

import { formatTimeFull, resolveTradeDisplaySide, type TradeDisplaySide } from './tradeTheme';
import { formatClobQtyLots } from '~/config/clobConfig';
import type { PushHubOrderRow } from '~/hooks/pushHubClient';

export type BottomTab = 'open' | 'history' | 'trades';

export function TradeBottomPanel({
    tab,
    onTabChange,
    openOrders,
    orderHistory,
    marketTrades,
    myTrades,
    viewerAddress,
    onCancelOrder,
    cancellingId,
}: {
    tab: BottomTab;
    onTabChange: (t: BottomTab) => void;
    openOrders: PushHubOrderRow[];
    orderHistory: PushHubOrderRow[];
    marketTrades: ClobTradeRow[];
    myTrades: ClobTradeRow[];
    viewerAddress?: string;
    onCancelOrder: (orderId: string) => void;
    cancellingId?: string;
}) {
    const t = useT();
    const tabItems = [
        { value: 'open' as const, label: `${t('trade.openOrders')} (${openOrders.length})` },
        { value: 'history' as const, label: `${t('trade.orderHistory')} (${orderHistory.length})` },
        { value: 'trades' as const, label: t('trade.tradeHistory') },
    ];

    return (
        /* mt-2 md:mt-4 减小移动端顶部间距，p-3 md:p-4 让小屏内边距更紧凑 */
        <div className="mt-2 md:mt-4 rounded-xl border border-white/10 bg-[#0F172A] p-3 md:p-4 shadow-xl overflow-hidden w-full">
            <Tabs size="lg" value={tab} onValueChange={(v) => onTabChange(v as BottomTab)}>
                {/* overflow-x-auto no-scrollbar whitespace-nowrap: 确保多Tab在手机窄屏下可以横向滑动，防止挤压换行 */}
                <div className="mb-3 flex items-center gap-4 border-b border-white/5 pb-px overflow-x-auto no-scrollbar whitespace-nowrap scroll-smooth">
                    {tabItems.map((item) => {
                        const isActive = tab === item.value;
                        return (
                            <button
                                key={item.value}
                                type="button"
                                onClick={() => onTabChange(item.value)}
                                className={clsx(
                                    "relative pb-3 text-left transition-all duration-200 outline-none group text-sm font-semibold shrink-0 touch-manipulation",
                                    isActive ? "text-[#38BDF8]" : "text-[#aeb9d0] hover:text-white"
                                )}
                            >
                                <Heading variant="heading4/semibold" className="text-sm md:text-base">
                                    {item.label}
                                </Heading>
                                <span
                                    className={clsx(
                                        "absolute bottom-0 left-0 h-[2px] w-full transition-transform duration-200 origin-left",
                                        isActive ? "bg-[#38BDF8] scale-x-100" : "bg-transparent scale-x-0 group-hover:scale-x-50 group-hover:bg-gray-500"
                                    )}
                                />
                            </button>
                        );
                    })}
                </div>

                <TabsContent value="open" noGap>
                    <OpenOrdersTable
                        rows={openOrders}
                        viewerAddress={viewerAddress}
                        onCancel={onCancelOrder}
                        cancellingId={cancellingId}
                    />
                </TabsContent>
                <TabsContent value="history" noGap>
                    <OrderHistoryTable rows={orderHistory} />
                </TabsContent>
                <TabsContent value="trades" noGap>
                    <TradesTable rows={marketTrades.slice(0, 30)} />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function OpenOrdersTable({
    rows,
    viewerAddress,
    onCancel,
    cancellingId,
}: {
    rows: PushHubOrderRow[];
    viewerAddress?: string;
    onCancel: (id: string) => void;
    cancellingId?: string;
}) {
    const t = useT();
    if (!viewerAddress) {
        return (
            <div className="py-12 text-center text-[#aeb9d0] text-sm font-medium">
                {t('trade.connectToViewOrders')}
            </div>
        );
    }
    if (rows.length === 0) {
        return (
            <div className="py-12 text-center text-[#aeb9d0] text-sm font-medium">
                {t('trade.noOpenOrders')}
            </div>
        );
    }
    return (
        /* w-full overflow-x-auto: 移动端核心防撑破外壳设计，允许局部横向丝滑滚动 */
        <div className="w-full overflow-x-auto scrolling-touch">
            <table className="w-full text-left border-collapse min-w-[600px] md:min-w-full">
                <thead>
                    <tr className="border-b border-[#aeb9d0]/30 text-xs font-semibold text-[#aeb9d0] tracking-wider whitespace-nowrap">
                        <th className="pb-3 pr-3 font-medium">{t('common.time')}</th>
                        <th className="pb-3 pr-3 font-medium">{t('common.side')}</th>
                        <th className="pb-3 pr-3 font-medium text-right">{t('common.price')}</th>
                        <th className="pb-3 pr-3 font-medium text-right">{t('trade.orderQty')}</th>
                        <th className="pb-3 pr-3 font-medium text-right">{t('trade.remaining')}</th>
                        <th className="pb-3 pr-3 font-medium">{t('trade.orderId')}</th>
                        <th className="pb-3 text-right font-medium">{t('common.action')}</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-900/40 whitespace-nowrap">
                    {rows.map((r) => (
                        <tr key={r.orderId} className="hover:bg-[#1E222D]/20 transition-colors group">
                            <td className="py-2.5 md:py-3.5 pr-3 text-xs text-[#aeb9d0] font-medium tabular-nums">
                                {formatTimeFull(r.placedAtMs)}
                            </td>
                            <td className="py-2.5 md:py-3.5 pr-3">
                                <span className={clsx(
                                    'inline-flex items-center justify-center px-2 py-0.5 rounded text-[10px] font-extrabold tracking-wide uppercase border',
                                    r.isAsk
                                        ? 'border-orange-950/60 bg-orange-950/20 text-[#F97316]'
                                        : 'border-emerald-900/60 bg-emerald-950/30 text-[#10B981]'
                                )}>
                                    {r.isAsk ? t('common.sell') : t('common.buy')}
                                </span>
                            </td>
                            <td className="py-2.5 md:py-3.5 pr-3 tabular-nums text-sm font-bold text-[#aeb9d0] text-right">
                                {r.dataPerDgc}
                            </td>
                            <td className="py-2.5 md:py-3.5 pr-3 tabular-nums text-sm font-bold text-[#aeb9d0] text-right">
                                {formatClobQtyLots(r.originalQtyLots, r.placedAtMs)}
                            </td>
                            <td className="py-2.5 md:py-3.5 pr-3 tabular-nums text-sm font-bold text-[#38BDF8] text-right">
                                {formatClobQtyLots(r.remainingQtyLots, r.placedAtMs)}
                            </td>
                            <td className="py-2.5 md:py-3.5 pr-3 tabular-nums text-xs font-medium text-[#aeb9d0] group-hover:text-gray-400 transition-colors">
                                # {r.orderId}
                            </td>
                            <td className="py-2.5 md:py-3.5 text-right">
                                <button
                                    type="button"
                                    className="rounded border border-[#F97316] bg-rose-950/10 px-2.5 py-1 text-xs font-bold text-[#F97316] hover:bg-[#F97316]/30 active:scale-95 transition disabled:opacity-40 disabled:pointer-events-none touch-manipulation"
                                    disabled={cancellingId === r.orderId}
                                    onClick={() => onCancel(r.orderId)}
                                >
                                    {cancellingId === r.orderId ? t('trade.cancelling') : t('trade.cancelOrder')}
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function orderHistoryStatusLabel(
    status: PushHubOrderRow['status'],
    t: (key: string, fallback?: string) => string,
): string {
    switch (status) {
        case 'open':
            return t('trade.statusOpen');
        case 'filled':
            return t('trade.statusFilled');
        case 'cancelled':
            return t('trade.statusCancelled');
        case 'partial':
            return t('trade.statusPartial');
        default:
            return status;
    }
}

function OrderHistoryTable({ rows }: { rows: PushHubOrderRow[] }) {
    const t = useT();
    if (rows.length === 0) {
        return (
            <div className="py-12 text-center text-[#aeb9d0] text-sm font-medium">
                {t('trade.noOrderHistory')}
            </div>
        );
    }
    return (
        <div className="w-full overflow-x-auto scrolling-touch">
            <table className="w-full text-left border-collapse min-w-[550px] md:min-w-full">
                <thead>
                    <tr className="border-b border-[#aeb9d0]/30 text-xs font-semibold text-[#aeb9d0] tracking-wider whitespace-nowrap">
                        <th className="pb-3 pr-3 font-medium">{t('common.time')}</th>
                        <th className="pb-3 pr-3 font-medium">{t('common.side')}</th>
                        <th className="pb-3 pr-3 font-medium text-right">{t('common.price')}</th>
                        <th className="pb-3 pr-3 font-medium text-right">{t('common.quantity')}</th>
                        <th className="pb-3 pr-3 font-medium">{t('common.status')}</th>
                        <th className="pb-3 text-right font-medium">{t('trade.orderId')}</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-900/40 whitespace-nowrap">
                    {rows.map((r) => (
                        <tr key={r.orderId} className="hover:bg-[#1E222D]/20 transition-colors">
                            <td className="py-2.5 md:py-3.5 pr-3 text-xs text-[#aeb9d0] font-medium tabular-nums">
                                {formatTimeFull(r.closedAtMs ?? r.placedAtMs)}
                            </td>
                            <td className="py-2.5 md:py-3.5 pr-3">
                                <span className={clsx(
                                    'inline-flex items-center justify-center px-2 py-0.5 rounded text-[10px] font-extrabold tracking-wide uppercase border',
                                    r.isAsk
                                        ? 'border-orange-950/60 bg-orange-950/20 text-[#F97316]'
                                        : 'border-emerald-900/60 bg-emerald-950/30 text-[#10B981]'
                                )}>
                                    {r.isAsk ? t('common.sell') : t('common.buy')}
                                </span>
                            </td>
                            <td className="py-2.5 md:py-3.5 pr-3 tabular-nums text-sm font-bold text-[#aeb9d0] text-right">
                                {r.dataPerDgc}
                            </td>
                            <td className="py-2.5 md:py-3.5 pr-3 tabular-nums text-sm font-bold text-[#aeb9d0] text-right">
                                {formatClobQtyLots(r.originalQtyLots, r.placedAtMs)}
                                {r.status === 'partial' ? ` / ${t('trade.remainingShort')} ${formatClobQtyLots(r.remainingQtyLots, r.placedAtMs)}` : ''}
                            </td>
                            <td className="py-2.5 md:py-3.5 pr-3 text-xs font-medium text-[#aeb9d0]">
                                {orderHistoryStatusLabel(r.status, t)}
                            </td>
                            <td className="py-2.5 md:py-3.5 text-right tabular-nums text-xs font-medium text-[#aeb9d0]">
                                #{r.orderId}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function tradeSideLabel(side: TradeDisplaySide, t: (key: string, fallback?: string) => string): string {
    if (side === 'buy') return t('common.buy');
    if (side === 'sell') return t('common.sell');
    return '—';
}

function TradesTable({ rows }: { rows: ClobTradeRow[] }) {
    const t = useT();
    if (rows.length === 0) {
        return (
            <div className="py-12 text-center text-[#aeb9d0] text-sm font-medium">
                {t('trade.noTradeHistory')}
            </div>
        );
    }
    return (
        <div className="w-full overflow-x-auto scrolling-touch">
            <table className="w-full text-left border-collapse min-w-[500px] md:min-w-full">
                <thead>
                    <tr className="border-b border-gray-800/50 text-xs font-semibold text-[#aeb9d0] tracking-wider whitespace-nowrap">
                        <th className="pb-3 pr-3 font-medium">{t('common.time')}</th>
                        <th className="pb-3 pr-3 font-medium text-right">{t('common.price')}</th>
                        <th className="pb-3 pr-3 font-medium text-right">{t('common.quantity')}</th>
                        <th className="pb-3 pr-3 font-medium">{t('trade.taker')}</th>
                        <th className="pb-3 text-left font-medium">Tx</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-900/40 whitespace-nowrap">
                    {rows.map((row, i) => {
                        const displaySide: TradeDisplaySide =
                            row.tradeSide === 'buy' || row.tradeSide === 'sell'
                                ? row.tradeSide
                                : resolveTradeDisplaySide(row, rows[i + 1]);
                        const isSell = displaySide === 'sell';
                        const directionText = tradeSideLabel(displaySide, t);

                        return (
                            <tr key={`${row.digest}-${row.priceTicks}`} className="hover:bg-[#1E222D]/20 transition-colors group">
                                <td className="py-2.5 md:py-3.5 pr-3 tabular-nums text-xs text-[#aeb9d0] font-medium">
                                    {formatTimeFull(row.timestampMs)}
                                </td>
                                <td
                                    className={clsx(
                                        'py-2.5 md:py-3.5 pr-3 tabular-nums text-sm font-bold text-right',
                                        isSell ? 'text-[#F97316]' : 'text-[#10B981]',
                                    )}
                                >
                                    {row.dataPerDgc}
                                </td>
                                <td className="py-2.5 md:py-3.5 pr-3 tabular-nums text-sm font-bold text-[#aeb9d0] text-right">
                                    {formatClobQtyLots(row.qtyLots, row.timestampMs)}
                                </td>
                                <td className="py-2.5 md:py-3.5 pr-3">
                                    <span className={clsx(
                                        'inline-flex items-center justify-center px-2 py-0.5 rounded text-[10px] font-extrabold tracking-wide uppercase border',
                                        isSell
                                            ? 'border-orange-950/60 bg-orange-950/20 text-[#F97316]'
                                            : 'border-emerald-900/60 bg-emerald-950/30 text-[#10B981]'
                                    )}>
                                        {directionText !== '—' ? directionText : isSell ? t('common.sell') : t('common.buy')}
                                    </span>
                                </td>
                                <td className="py-2.5 md:py-3.5 text-left text-xs">
                                    {row.digest ? (
                                        <Link
                                            to={`/txblock/${row.digest}`}
                                            className="text-[#38BDF8] hover:text-[#7dd3fc] font-medium transition-colors touch-manipulation"
                                        >
                                            {row.digest.slice(0, 4)}...{row.digest.slice(-4)}
                                        </Link>
                                    ) : (
                                        <span className="text-gray-600">—</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
